import React, { useEffect } from 'react';
import {
  MessageCircle,
  ArrowRight,
  Check,
  Mail,
  Facebook,
  MessageSquare,
  Phone,
  Smartphone,
  AlertTriangle,
  Target,
  Zap,
  Clock
} from 'lucide-react';

const CustomerSupportMultipleChannels = () => {
  useEffect(() => {
    // Google Analytics
    window.dataLayer = window.dataLayer || [];
    function gtag(){window.dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', 'G-V8ESDMZ5NZ');

    // SEO-OPTIMIZED: Title and meta description
    document.title = 'How to Handle Customer Support Across Multiple Channels | TicketDesk';

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = 'Managing customer inquiries from Gmail, Facebook, WhatsApp, and your website? Learn how to unify multi-channel support without losing your mind or dropping messages.';

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = 'https://www.ticketdesk.app/blog/customer-support-multiple-channels';

    // Structured data for article
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "How to Handle Customer Support Across Multiple Channels",
      "description": "Complete guide to managing multi-channel customer support for small businesses including email, social media, messaging apps, and website chat",
      "author": {
        "@type": "Organization",
        "name": "TicketDesk"
      },
      "publisher": {
        "@type": "Organization",
        "name": "TicketDesk",
        "logo": {
          "@type": "ImageObject",
          "url": "https://ticketdesk.app/logo.png"
        }
      },
      "datePublished": "2025-01-12",
      "dateModified": "2025-01-12"
    };

    let script = document.querySelector('script[type="application/ld+json"]');
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(structuredData);
  }, []);

  const trackEvent = (eventName, eventParams = {}) => {
    if (window.gtag) {
      window.gtag('event', eventName, eventParams);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <a href="/" className="flex items-center min-w-0">
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-message-circle">
                  <path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719"></path>
                </svg>
              </div>
              <span className="ml-2 sm:ml-3 text-lg sm:text-xl font-semibold text-gray-900">TicketDesk</span>
            </a>
            <div className="flex items-center space-x-2 sm:space-x-4 lg:space-x-8">
              <a href="/blog" className="text-xs sm:text-sm text-purple-600 font-medium hidden md:block">Blog</a>
              <a href="/#features" className="text-xs sm:text-sm text-gray-700 hover:text-gray-900 hidden md:block">Features</a>
              <a href="/#pricing" className="text-xs sm:text-sm text-gray-700 hover:text-gray-900">Pricing</a>
              <a href="/login" className="text-xs sm:text-sm text-gray-700 hover:text-gray-900 hidden lg:block">Sign In</a>
              <a href="/register" className="bg-purple-600 text-white px-3 sm:px-4 lg:px-5 py-1.5 sm:py-2 rounded text-xs sm:text-sm font-medium hover:bg-purple-700 whitespace-nowrap">
                Try free
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Article Header */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <a href="/blog" className="text-purple-600 hover:text-purple-700 font-medium text-sm inline-flex items-center mb-4">
            ← Back to Blog
          </a>
          <div className="mb-4">
            <span className="inline-block bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full">
              Best Practices
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            How to Handle Customer Support Across Multiple Channels
          </h1>
          <div className="flex items-center text-gray-600 text-sm">
            <Clock className="w-4 h-4 mr-1" />
            <span className="mr-4">10 min read</span>
            <span>January 12, 2025</span>
          </div>
        </div>

        {/* Article Content */}
        <div className="prose prose-lg max-w-none">
          <p className="text-xl text-gray-700 leading-relaxed mb-6">
            Your customers don't think about "channels." They just want to reach you however it's convenient for them—email today, Facebook tomorrow, maybe WhatsApp for urgent questions. But on your end? Managing inquiries across Gmail, Facebook Messenger, Instagram DMs, your website chat widget, and possibly WhatsApp or SMS creates chaos. Messages get lost, customers send the same question twice on different channels, and you're constantly switching between tabs trying to remember what you already answered.
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            This is the multi-channel support challenge facing every small business in 2025. Customers expect fast, consistent responses regardless of how they contact you. Failing to deliver means lost sales, frustrated customers, and 1-star reviews complaining about "terrible communication." The solution isn't hiring more people or working longer hours—it's unifying all those channels into one manageable system.
          </p>

          <div className="bg-red-50 border-l-4 border-red-500 p-6 my-8 rounded-r-lg">
            <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2 text-red-500" />
              The Cost of Fragmented Support
            </h3>
            <p className="text-gray-700 mb-2">Businesses with poor multi-channel management lose customers to:</p>
            <ul className="space-y-1 text-gray-700 list-disc list-inside">
              <li>Missed messages that never get answered</li>
              <li>Duplicate responses from multiple team members</li>
              <li>Slow response times while switching between platforms</li>
              <li>Inconsistent answers across different channels</li>
              <li>Customers repeating questions on multiple channels out of frustration</li>
            </ul>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Why Multi-Channel Support Is Hard</h2>

          <p className="text-gray-700 leading-relaxed mb-6">
            Before jumping into solutions, let's acknowledge why this problem exists. It's not because you're disorganized or bad at customer service—it's because the systems themselves create problems:
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Each Platform Lives in Its Own Silo</h3>

          <p className="text-gray-700 leading-relaxed mb-6">
            Gmail doesn't talk to Facebook. Facebook doesn't know about your website chat. Your website widget has its own separate inbox. This means you're constantly checking 4-5 different places to see if anyone needs help. Miss one? That customer waits hours or gives up entirely.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">No Unified Customer View</h3>

          <p className="text-gray-700 leading-relaxed mb-6">
            A customer emails you Monday asking about product availability. Tuesday they message on Facebook asking the same thing—they forgot they already emailed, or they think Facebook will be faster. Without a unified system, you don't know this is the same person with the same question. You respond twice, wasting time. Or worse, two different team members give conflicting answers.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Different Response Expectations Per Channel</h3>

          <p className="text-gray-700 leading-relaxed mb-6">
            Customers expect email responses within 24 hours. Facebook messages? They want answers within 60 minutes. Live chat? Instantly. Managing these different expectations across channels is mentally exhausting when each platform requires separate attention and prioritization.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Team Coordination Falls Apart</h3>

          <p className="text-gray-700 leading-relaxed mb-6">
            Who's handling the Facebook messages? Did anyone check Instagram this morning? Is someone watching the website chat? When support channels are fragmented, team coordination breaks down. You need constant verbal check-ins ("Did you answer that guy on Facebook?") to prevent messages from falling through cracks.
          </p>

          <div className="bg-blue-50 border border-blue-200 p-6 my-8 rounded-lg">
            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
              <Target className="w-5 h-5 mr-2 text-blue-600" />
              What Multi-Channel Support Should Look Like
            </h3>
            <ul className="space-y-2 text-gray-700 list-disc list-inside">
              <li>All customer messages in one unified inbox regardless of source</li>
              <li>Each conversation tracked as a ticket with status (new, in progress, resolved)</li>
              <li>Clear assignment so team members know who's handling what</li>
              <li>Customer history visible—see previous conversations across all channels</li>
              <li>Respond from one interface without switching to Gmail/Facebook/WhatsApp</li>
              <li>Automated routing and prioritization based on channel and urgency</li>
            </ul>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Step-by-Step: Building Your Multi-Channel System</h2>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Step 1: Choose Your Essential Channels</h3>

          <p className="text-gray-700 leading-relaxed mb-6">
            Don't try to be everywhere. Focus on channels your customers actually use. For most small businesses, this means:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
            <div className="bg-white border-2 border-gray-200 rounded-lg p-5">
              <Mail className="w-8 h-8 text-blue-600 mb-3" />
              <h4 className="font-bold text-gray-900 mb-2">Email (Gmail)</h4>
              <p className="text-sm text-gray-700">Foundation for most businesses. Professional, documented, customers expect it.</p>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-lg p-5">
              <Facebook className="w-8 h-8 text-blue-500 mb-3" />
              <h4 className="font-bold text-gray-900 mb-2">Facebook Messenger</h4>
              <p className="text-sm text-gray-700">Huge user base. Customers message business pages like friends. Fast expectation.</p>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-lg p-5">
              <MessageSquare className="w-8 h-8 text-green-600 mb-3" />
              <h4 className="font-bold text-gray-900 mb-2">WhatsApp Business</h4>
              <p className="text-sm text-gray-700">International customers prefer it. Personal feel. Good for order updates.</p>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-lg p-5">
              <MessageCircle className="w-8 h-8 text-purple-600 mb-3" />
              <h4 className="font-bold text-gray-900 mb-2">Website Chat Widget</h4>
              <p className="text-sm text-gray-700">Catch visitors in buying mode. Instant response expected. High conversion potential.</p>
            </div>
          </div>

          <p className="text-gray-700 leading-relaxed mb-6">
            Trying to manage more than 4-5 channels as a small business stretches resources thin. Master these core channels before adding Instagram DMs, Twitter, LinkedIn, or SMS.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Step 2: Implement Unified Inbox Software</h3>

          <p className="text-gray-700 leading-relaxed mb-6">
            You cannot manage multi-channel support with browser tabs. Period. You need software that pulls all channels into one unified inbox where every message becomes a trackable ticket. This is non-negotiable for staying organized.
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            <strong>What to look for in unified inbox software:</strong>
          </p>

          <ul className="space-y-3 text-gray-700 mb-6 ml-6">
            <li className="flex items-start">
              <Check className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              <span><strong>Native integrations</strong> for Gmail, Facebook, WhatsApp—not manual forwarding</span>
            </li>
            <li className="flex items-start">
              <Check className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              <span><strong>Automatic ticket creation</strong> from every channel without manual sorting</span>
            </li>
            <li className="flex items-start">
              <Check className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              <span><strong>Team assignment capabilities</strong> so everyone knows who owns what</span>
            </li>
            <li className="flex items-start">
              <Check className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              <span><strong>Status tracking</strong> (new, in progress, resolved) for every conversation</span>
            </li>
            <li className="flex items-start">
              <Check className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              <span><strong>Reply from the interface</strong> without jumping to Gmail or Facebook</span>
            </li>
            <li className="flex items-start">
              <Check className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              <span><strong>Customer conversation history</strong> showing all previous interactions across channels</span>
            </li>
          </ul>

          <p className="text-gray-700 leading-relaxed mb-6">
            TicketDesk was built specifically for this use case. Connect Gmail in 60 seconds, add Facebook and WhatsApp, and all customer messages flow into one inbox as trackable tickets. Your team assigns tickets, responds from one interface, and nothing gets lost.
          </p>

          <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-8 my-12 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Unify Your Customer Support Channels</h3>
            <p className="text-gray-700 mb-6">
              Connect Gmail, Facebook, and WhatsApp in minutes. Start handling all customer messages from one inbox.
            </p>
            <a
              href="/register"
              onClick={() => trackEvent('cta_click', { location: 'multi_channel_midcontent' })}
              className="inline-flex items-center justify-center bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-purple-700"
            >
              Start Free Trial
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
            <p className="text-sm text-gray-600 mt-4">No credit card required • Free plan available</p>
          </div>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Step 3: Create Channel-Specific Response Guidelines</h3>

          <p className="text-gray-700 leading-relaxed mb-6">
            Not all channels deserve identical treatment. Set clear internal guidelines for response times and communication style:
          </p>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 my-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-300">
                  <th className="text-left py-3 font-bold text-gray-900">Channel</th>
                  <th className="text-left py-3 font-bold text-gray-900">Response Time</th>
                  <th className="text-left py-3 font-bold text-gray-900">Style</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                <tr className="border-b border-gray-200">
                  <td className="py-3">Email</td>
                  <td className="py-3">Within 24 hours</td>
                  <td className="py-3">Professional, detailed</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-3">Facebook</td>
                  <td className="py-3">Within 60 minutes</td>
                  <td className="py-3">Friendly, conversational</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-3">WhatsApp</td>
                  <td className="py-3">Within 2 hours</td>
                  <td className="py-3">Brief, personal</td>
                </tr>
                <tr>
                  <td className="py-3">Website Chat</td>
                  <td className="py-3">Instant (or clear offline msg)</td>
                  <td className="py-3">Quick, helpful</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-gray-700 leading-relaxed mb-6">
            Share these guidelines with your team. When unified inbox software labels each ticket with its source channel, your team automatically knows response urgency and communication style.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Step 4: Use Automation Strategically</h3>

          <p className="text-gray-700 leading-relaxed mb-6">
            Automation should handle repetitive tasks, not replace human responses. Smart automation for multi-channel support includes:
          </p>

          <div className="space-y-4 my-6">
            <div className="bg-white border-l-4 border-blue-500 p-5 rounded-r-lg">
              <h4 className="font-bold text-gray-900 mb-2 flex items-center">
                <Zap className="w-5 h-5 mr-2 text-blue-600" />
                Auto-Acknowledgment
              </h4>
              <p className="text-sm text-gray-700">When someone emails, auto-reply: "Thanks! We received your message and will respond within 24 hours. Ticket #ABC123." Sets expectations and confirms receipt.</p>
            </div>

            <div className="bg-white border-l-4 border-purple-500 p-5 rounded-r-lg">
              <h4 className="font-bold text-gray-900 mb-2 flex items-center">
                <Target className="w-5 h-5 mr-2 text-purple-600" />
                Auto-Assignment by Channel
              </h4>
              <p className="text-sm text-gray-700">Route all Facebook messages to Sarah (she's fast with social media). Email goes to Mike (detail-oriented). Automates workflow without manual sorting.</p>
            </div>

            <div className="bg-white border-l-4 border-green-500 p-5 rounded-r-lg">
              <h4 className="font-bold text-gray-900 mb-2 flex items-center">
                <MessageCircle className="w-5 h-5 mr-2 text-green-600" />
                Canned Responses for FAQs
              </h4>
              <p className="text-sm text-gray-700">Save templates for common questions (shipping times, return policy). Don't auto-send—but let agents insert with one click. Speeds up responses without sounding robotic.</p>
            </div>
          </div>

          <p className="text-gray-700 leading-relaxed mb-6">
            Never use automation that makes customers feel like they're talking to a bot. Auto-acknowledgment is fine. Auto-responses to specific questions? Dangerous—customers spot them immediately and feel dismissed.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Step 5: Train Your Team on Unified Workflows</h3>

          <p className="text-gray-700 leading-relaxed mb-6">
            Once you have unified inbox software, your team needs clear processes:
          </p>

          <ul className="space-y-3 text-gray-700 mb-6 ml-6">
            <li className="flex items-start">
              <span className="font-bold mr-2 text-purple-600">1.</span>
              <span><strong>Start each day checking the unified inbox</strong>, not individual platforms. Break the Gmail-then-Facebook-then-WhatsApp habit.</span>
            </li>
            <li className="flex items-start">
              <span className="font-bold mr-2 text-purple-600">2.</span>
              <span><strong>Claim tickets by assigning them to yourself</strong> before responding. Prevents duplicate responses.</span>
            </li>
            <li className="flex items-start">
              <span className="font-bold mr-2 text-purple-600">3.</span>
              <span><strong>Use internal notes for complex issues</strong>. If a customer asks something you don't know, tag a teammate in notes rather than delaying response.</span>
            </li>
            <li className="flex items-start">
              <span className="font-bold mr-2 text-purple-600">4.</span>
              <span><strong>Mark tickets resolved when complete</strong>. Keeps inbox clean and shows progress metrics.</span>
            </li>
            <li className="flex items-start">
              <span className="font-bold mr-2 text-purple-600">5.</span>
              <span><strong>Check customer history before responding</strong>. Seeing previous conversations prevents re-asking questions the customer already answered.</span>
            </li>
          </ul>

          <p className="text-gray-700 leading-relaxed mb-6">
            Training takes 30 minutes. The productivity gains are immediate—team members stop asking "Did anyone handle the Facebook guy?" because ticket assignment makes ownership obvious.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Common Multi-Channel Mistakes to Avoid</h2>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Mistake #1: Treating All Channels Identically</h3>

          <p className="text-gray-700 leading-relaxed mb-6">
            Customers who email expect professional, detailed responses. Facebook Messenger users want quick, casual replies. Treating WhatsApp like formal email feels stuffy. Adapt your tone to match channel expectations—unified inbox software shouldn't mean unified communication style.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Mistake #2: Over-Automating Responses</h3>

          <p className="text-gray-700 leading-relaxed mb-6">
            Customers hate canned responses that don't actually answer their question. Use automation for acknowledgment and routing, not for answering. Real support requires human judgment—automation should make your team faster, not replace them.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Mistake #3: Ignoring Channel-Specific Analytics</h3>

          <p className="text-gray-700 leading-relaxed mb-6">
            Track response times per channel, not just overall. You might be crushing email (12-hour average) while Facebook messages wait 2 days. Channel-specific metrics reveal where to focus improvement efforts.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Mistake #4: Adding Channels Too Fast</h3>

          <p className="text-gray-700 leading-relaxed mb-6">
            Master 2-3 channels before adding more. Adding Instagram, Twitter, LinkedIn, and SMS simultaneously overwhelms your team and degrades response quality everywhere. Nail email and Facebook first, then expand thoughtfully.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Measuring Multi-Channel Success</h2>

          <p className="text-gray-700 leading-relaxed mb-6">
            Track these metrics to ensure your multi-channel system actually works:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
              <h4 className="font-bold text-gray-900 mb-2">Average Response Time</h4>
              <p className="text-sm text-gray-700">How quickly do customers get their first reply? Aim for under 24 hours for email, under 60 minutes for social channels.</p>
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-5">
              <h4 className="font-bold text-gray-900 mb-2">Resolution Rate</h4>
              <p className="text-sm text-gray-700">What percentage of tickets get resolved vs. abandoned? High abandonment suggests customers gave up waiting.</p>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-5">
              <h4 className="font-bold text-gray-900 mb-2">Channel Distribution</h4>
              <p className="text-sm text-gray-700">Where do most inquiries come from? Helps you prioritize resources and improve high-volume channels.</p>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-5">
              <h4 className="font-bold text-gray-900 mb-2">Customer Satisfaction</h4>
              <p className="text-sm text-gray-700">Simple post-resolution survey: "Did we solve your problem?" Reveals quality issues before they become reviews.</p>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Final Thoughts</h2>

          <p className="text-gray-700 leading-relaxed mb-6">
            Multi-channel customer support doesn't have to be chaotic. The key is unification—bringing all channels into one system where every message becomes a trackable, assignable ticket. Without this unification, you're fighting an unwinnable battle against browser tabs and scattered conversations.
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            Start simple: Connect your most important channels (probably Gmail and Facebook) to unified inbox software like TicketDesk. Get your team comfortable with the workflow—claim tickets, respond, mark resolved. Once this becomes habit, adding additional channels is easy because the foundation is solid.
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            Your customers don't care that managing multiple channels is hard. They just want answers. A unified approach lets you deliver those answers consistently and quickly, regardless of how customers choose to reach you.
          </p>

          <div className="bg-purple-600 text-white rounded-xl p-8 my-12 text-center">
            <h3 className="text-2xl font-bold mb-4">Ready to Unify Your Support Channels?</h3>
            <p className="text-lg mb-6 opacity-90">
              Connect Gmail, Facebook, and WhatsApp in 60 seconds. Manage all customer conversations from one inbox.
            </p>
            <a
              href="/register"
              onClick={() => trackEvent('cta_click', { location: 'multi_channel_bottom' })}
              className="inline-flex items-center justify-center bg-white text-purple-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100"
            >
              Start Free Trial
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
            <p className="text-sm mt-4 opacity-75">No credit card required • Free plan available</p>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16 mt-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div className="col-span-2">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719"></path>
                  </svg>
                </div>
                <span className="ml-3 text-xl font-bold">TicketDesk</span>
              </div>
              <p className="text-gray-400 max-w-md">
                Customer support software for small businesses. Unify Gmail, Facebook, WhatsApp, and review monitoring in one simple inbox.
              </p>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Product</h3>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li><a href="/#features" className="hover:text-white">Features</a></li>
                <li><a href="/#pricing" className="hover:text-white">Pricing</a></li>
                <li><a href="/for-ecommerce" className="hover:text-white">For E-commerce</a></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Resources</h3>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li><a href="/blog" className="hover:text-white">Blog</a></li>
                <li><a href="/compare/zendesk" className="hover:text-white">vs Zendesk</a></li>
                <li><a href="/register" className="hover:text-white">Start Free Trial</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; 2025 TicketDesk. Customer support software for small businesses.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CustomerSupportMultipleChannels;
